/**
 * Canvas and sampling primitives.
 *
 * Everything downstream works in one of two spaces: the *composite* space,
 * which is the resolution the finished photograph is written at, and the
 * *analysis* space, a few hundred pixels wide, where all the searching
 * happens. Keeping the two apart is what lets the output stay sharp while
 * the maths stays cheap.
 */
import type { Gray } from './types';

export type AnyCanvas = HTMLCanvasElement | OffscreenCanvas;

/** A canvas of the given size, off-document. */
export function mk(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  return c;
}

/**
 * A 2D context flagged for repeated `getImageData`. Without the hint the
 * browser keeps the canvas on the GPU and every read costs a round trip.
 */
export function ctxOf(c: HTMLCanvasElement): CanvasRenderingContext2D {
  const x = c.getContext('2d', { willReadFrequently: true });
  if (!x) throw new Error('2D canvas context unavailable');
  return x;
}

/** A plain drawing context, for canvases that are written far more than read. */
export function drawCtx(c: HTMLCanvasElement): CanvasRenderingContext2D {
  const x = c.getContext('2d');
  if (!x) throw new Error('2D canvas context unavailable');
  return x;
}

/**
 * Downsample a canvas so its LONG edge is `longEdge`, and reduce it to
 * luminance. Budgeting the long edge rather than the width is what keeps the
 * analysis grids — and so the search — the same size in portrait and landscape.
 */
export function grayOf(
  cv: CanvasImageSource & { width: number; height: number },
  longEdge: number,
): Gray {
  const scale = longEdge / Math.max(cv.width, cv.height);
  const tw = Math.max(1, Math.round(cv.width * scale));
  const th = Math.max(1, Math.round(cv.height * scale));
  const t = mk(tw, th);
  const x = ctxOf(t);
  x.imageSmoothingEnabled = true;
  x.imageSmoothingQuality = 'high';
  x.drawImage(cv, 0, 0, tw, th);
  return grayOfImageData(x.getImageData(0, 0, tw, th));
}

/** Reduce already-extracted RGBA to luminance. */
export function grayOfImageData(img: ImageData): Gray {
  const d = img.data;
  const g = new Float32Array(img.width * img.height);
  for (let i = 0, p = 0; i < g.length; i++, p += 4) {
    g[i] = 0.299 * d[p] + 0.587 * d[p + 1] + 0.114 * d[p + 2];
  }
  return { g, w: img.width, h: img.height };
}

/**
 * Bilinear sample. Returns -1 outside the image rather than clamping, because
 * every caller needs to tell "no data here" apart from "black here".
 */
export function sampleBil(g: Float32Array, w: number, h: number, x: number, y: number): number {
  if (x < 0 || y < 0 || x > w - 2 || y > h - 2) return -1;
  const x0 = x | 0;
  const y0 = y | 0;
  const fx = x - x0;
  const fy = y - y0;
  const i = y0 * w + x0;
  return (
    g[i] * (1 - fx) * (1 - fy) +
    g[i + 1] * fx * (1 - fy) +
    g[i + w] * (1 - fx) * fy +
    g[i + w + 1] * fx * fy
  );
}

/**
 * Area-average a grayscale image down to a smaller long edge.
 *
 * The three analysis grids used to come from three separate canvas draws and
 * three `getImageData` readbacks per frame. Reading pixels back off a canvas is
 * the expensive half of that, so the fine grid is now read once and the coarser
 * two are derived here, in arrays. Box-averaging rather than sampling matters:
 * the detector thresholds on how much the frames disagree, and aliasing would
 * read as disagreement.
 */
export function resampleGray(src: Gray, longEdge: number): Gray {
  const scale = longEdge / Math.max(src.w, src.h);
  if (scale >= 1) return src;

  const w = Math.max(1, Math.round(src.w * scale));
  const h = Math.max(1, Math.round(src.h * scale));
  const g = new Float32Array(w * h);
  const spanX = src.w / w;
  const spanY = src.h / h;

  for (let y = 0; y < h; y++) {
    const y0 = Math.floor(y * spanY);
    const y1 = Math.max(y0 + 1, Math.min(src.h, Math.ceil((y + 1) * spanY)));
    for (let x = 0; x < w; x++) {
      const x0 = Math.floor(x * spanX);
      const x1 = Math.max(x0 + 1, Math.min(src.w, Math.ceil((x + 1) * spanX)));
      let sum = 0;
      let n = 0;
      for (let sy = y0; sy < y1; sy++) {
        const row = sy * src.w;
        for (let sx = x0; sx < x1; sx++) {
          sum += src.g[row + sx];
          n++;
        }
      }
      g[y * w + x] = sum / n;
    }
  }
  return { g, w, h };
}

/** Mean gradient magnitude — a cheap, monotone stand-in for sharpness. */
export function sharpnessOf(gr: Gray): number {
  let s = 0;
  let n = 0;
  for (let y = 2; y < gr.h - 2; y += 2) {
    for (let x = 2; x < gr.w - 2; x += 2) {
      const i = y * gr.w + x;
      const dx = gr.g[i + 1] - gr.g[i - 1];
      const dy = gr.g[i + gr.w] - gr.g[i - gr.w];
      s += Math.hypot(dx, dy);
      n++;
    }
  }
  return n ? s / n : 0;
}
